import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'

import { config } from './baseTheme'

type BaseColorPalette =
  | 'blue'
  | 'pink'
  | 'red'
  | 'green'
  | 'orange'
  | 'gray'
  | 'purple'
  | 'cyan'
  | 'teal'
  | 'yellow'

// Creates a partner theme with a solid brand color and gradient overrides.
// Most partners follow this pattern — only override if custom gradients are needed (e.g. MaxAssist).
//
// `solid` is the ONLY brand token a partner replaces. Per the design tokens,
// fg/subtle/muted/emphasized/focusRing stay on the base ramp's 700/100/200/
// 300/400 steps, so they keep that ramp's hue rather than following the
// partner color. Deriving them from `brandColor` looks plausible but drifts
// well off the specified ramp (worst case ~Δ84 for Dentrix).
const createPartnerTheme = (
  brandColor: string,
  basePalette: BaseColorPalette = 'blue',
) => {
  const solidGradient = `linear-gradient(90deg, ${brandColor} 0%, ${brandColor} 99%)`
  const partnerConfig = defineConfig({
    theme: {
      tokens: {
        colors: {
          gradients: {
            mangoBrand: {
              button: { value: brandColor },
              notification_banner: { value: solidGradient },
              popup_background: { value: solidGradient },
              new_action_fab_button: { value: solidGradient },
            },
          },
        },
      },
      semanticTokens: {
        colors: {
          brand: {
            ...(config.theme?.semanticTokens?.colors?.[basePalette] || {}),
            solid: {
              value: { base: brandColor, _dark: brandColor },
            },
          },
        },
      },
    },
  })
  return createSystem(defaultConfig, config, partnerConfig)
}

// MaxAssist has custom gradients, so it doesn't use createPartnerTheme
const MaxAssistBrandColor = '#A4278F'
const maxAssistConfig = defineConfig({
  theme: {
    tokens: {
      colors: {
        gradients: {
          mangoBrand: {
            button: { value: MaxAssistBrandColor },
            notification_banner: {
              value: `linear-gradient(90deg,#FFF 2.67%, #D8A5D1 97.7%)`,
            },
            popup_background: {
              value: `linear-gradient(180deg, #FFF 2.67%, #D8A5D1 97.7%)`,
            },
            new_action_fab_button: {
              value: `linear-gradient(90deg, #A4278F 0%, #A4278F 59%, #A4278F 99%)`,
            },
          },
        },
      },
    },
    semanticTokens: {
      colors: {
        brand: {
          ...(config.theme?.semanticTokens?.colors?.pink || {}),
          solid: {
            value: { base: MaxAssistBrandColor, _dark: MaxAssistBrandColor },
          },
        },
      },
    },
  },
})

export const maxAssistTheme = createSystem(
  defaultConfig,
  config,
  maxAssistConfig,
)

export const dentrixTheme = createPartnerTheme('#003C80')
export const dentallyTheme = createPartnerTheme('#2563EB')
export const denticonTheme = createPartnerTheme('#1D73BE')
